import { useState } from 'react'
import classnames from 'classnames'
import { useQuery } from '@tanstack/react-query'
import { omit } from 'lodash'
import HeartRating from 'components/heart-rating'
import Modal from '../../../baby-design/components/Modal/Modal'
import LoadingDots from '../../../baby-design/components/LoadingDots/LoadingDots'
import Link from 'components/link'
import Image from 'components/image'
import Quantity from 'components/quantity'
// @ts-ignore
import AddToCartButton from 'store/components/add-to-cart-button'
// @ts-ignore
import AddToRegistryButton from 'store/components/add-to-registry-button'
// @ts-ignore
import { addToBabylist, getProduct } from 'store/api'
import { successMessage } from 'lib/flash-message'
import { pdpPath } from 'lib/urls'
import SelectionControls from '../product-details-page/selection-controls'
import { parseErrorMessage } from 'lib/api-utils'
import css from './QuickViewModal.styles.scss'
import { CurrentUser } from '../../../global'
import {
  SOURCE_QUICK_VIEW,
  ELECTRONIC_GIFT_CARD_MIN,
  ELECTRONIC_GIFT_CARD_MAX,
} from 'shared/constants'
import PriceTag, { PriceTagProps } from '../../components/PriceTag/PriceTag'
import { ProductCallouts } from 'components/ProductCallouts/ProductCallouts'

interface QuickViewModalProps {
  productId: number
  onHide(): void
  show: boolean
  onAddToCartSuccess?: (productId: number, quantity: number) => void
  onAddToRegistrySuccess?: (
    regItem: any,
    updatedCurrentUser?: CurrentUser
  ) => void
  onProductLinkClick?: () => void
  showAddToRegistryButton?: boolean
}

const QuickViewModal = ({
  productId,
  onHide,
  show,
  onAddToCartSuccess = () => {},
  onAddToRegistrySuccess = () => {},
  onProductLinkClick = () => {},
  showAddToRegistryButton = false,
}: QuickViewModalProps) => {
  const { data, isLoading } = useQuery({
    queryKey: [productId],
    queryFn: () => getProduct(productId),
    refetchOnWindowFocus: false,
  })

  const handleOnAddToCartSuccess = (productId: number, quantity: number) => {
    onAddToCartSuccess(productId, quantity)
    successMessage(`${data?.genericProduct?.shortName} added to cart!`)
    onHide()
  }

  return (
    <Modal
      className={css.QuickViewModal}
      isOpen={show}
      handleClose={onHide}
      showCloseButton
    >
      {!isLoading && data?.genericProduct && (
        <QuickViewModalContent
          genericProduct={data.genericProduct}
          onAddToCartSuccess={handleOnAddToCartSuccess}
          onAddToRegistrySuccess={onAddToRegistrySuccess}
          onProductLinkClick={onProductLinkClick}
          showAddToRegistryButton={showAddToRegistryButton}
        />
      )}
      {isLoading && (
        <div className={css['QuickViewModal__loader']}>
          <LoadingDots />
        </div>
      )}
    </Modal>
  )
}

interface Price {
  msrp?: number
  min?: number
  max?: number
  current?: number
}

const getPriceTagProps = (
  price?: Price,
  isElectronicGiftCard?: boolean
): PriceTagProps | null => {
  if (!price) return null

  if (isElectronicGiftCard) {
    return {
      minPrice: ELECTRONIC_GIFT_CARD_MIN,
      maxPrice: ELECTRONIC_GIFT_CARD_MAX,
      truncateZeroCents: true,
    }
  }

  return {
    msrp: price?.msrp,
    minPrice: price?.min,
    maxPrice: price?.max,
    currentPrice: price?.current,
  }
}

export const QuickViewModalContent = ({
  genericProduct,
  onAddToCartSuccess,
  onAddToRegistrySuccess,
  onProductLinkClick,
  showAddToRegistryButton,
}: any) => {
  const {
    activeProductId,
    attributes,
    brand,
    id,
    rating,
    reviewCount,
    shortName,
    slug,
    products,
    productAttributeMap,
  } = genericProduct
  const [activeProduct, setActiveProduct] = useState(
    products[activeProductId] || null
  )
  const [quantity, setQuantity] = useState(1)
  const {
    callouts = [],
    merchandisingMessage = '',
    showPurchaseQuantityLimit: showQuantityLimit = false,
    purchaseQuantityLimit: quantityLimit = 10,
    isElectronicGiftCard = false,
    availableOnBabylist,
    isCashFund,
    isFavor,
    isGiftCard,
    isCrowdfund,
  } = activeProduct || {}

  const productLinkUrl = activeProduct
    ? pdpPath(slug, id, activeProduct.id)
    : ''

  const disableAddToCart = !availableOnBabylist || isCashFund || isFavor
  const showQuantitySelector = !isGiftCard && !isCashFund && !isCrowdfund
  const supportsRatings =
    !isCashFund &&
    !isFavor &&
    !isElectronicGiftCard &&
    !isCrowdfund &&
    !!reviewCount

  const priceTagProps = getPriceTagProps(
    activeProduct.price,
    isElectronicGiftCard
  )

  const productPath = (productId: number) => pdpPath(slug, id, productId)

  const pdpLinkDecorator = (child: any) => (
    <Link
      onClick={onProductLinkClick}
      className={css['QuickViewModal__pdp-link']}
      url={productLinkUrl}
      newWindow
    >
      {child}
    </Link>
  )

  const addToRegistryAction = (registryId: number): Promise<any> => {
    return addToBabylist({
      quantity,
      product_id: activeProduct.id,
      registryId: registryId,
      source: SOURCE_QUICK_VIEW,
    }).catch((resp: any) => {
      PubSub.publish(BLConstants.FLASH_MESSAGE, {
        message: parseErrorMessage(resp),
        variant: 'danger',
      })
    })
  }

  return (
    <div className={css['QuickViewModal__layout']}>
      <div>
        {pdpLinkDecorator(
          <Image src={activeProduct?.images[0]?.url} width="100%" />
        )}
      </div>
      <div>
        <div className={classnames(css['QuickViewModal__contentWrapper'])}>
          <h3 className="flex col unset-all">
            {brand && (
              <div
                data-testid="brand"
                className={classnames(css['QuickViewModal__brand'])}
              >
                {brand}
              </div>
            )}
            <div
              data-testid="title"
              className={classnames(css['QuickViewModal__title'])}
            >
              {shortName}
            </div>
          </h3>
          {supportsRatings && (
            <HeartRating rating={rating || 0} reviewCount={reviewCount || 0} />
          )}
          <PriceTag {...priceTagProps} />
          {merchandisingMessage && (
            <div
              className={classnames(
                css['QuickViewModal__merchandisingMessage']
              )}
            >
              {merchandisingMessage}
            </div>
          )}
          {callouts && (
            <div className={classnames(css['QuickViewModal__callouts'])}>
              <ProductCallouts callouts={callouts} textSize={13} />
            </div>
          )}
          {pdpLinkDecorator('See full details')}
        </div>
        <SelectionControls
          allAttributes={attributes}
          attributeLabelOverrides={
            activeProduct.isCashFund ? { amount: 'goal' } : null
          }
          /* eslint-disable-next-line react/jsx-no-bind */
          onProductChange={(productId: any) =>
            setActiveProduct(products[productId])
          }
          productAttributeMap={productAttributeMap}
          products={products}
          productPath={productPath}
          selectedAttributes={omit(activeProduct.attributes, 'sizeOnCatalog')}
        />
        {showQuantitySelector && (
          <Quantity
            className="mtm mbl"
            min={1}
            max={quantityLimit}
            onChange={(qty: number) => setQuantity(qty)}
            limit={showQuantityLimit ? quantityLimit : null}
            value={quantity}
          />
        )}
        {showAddToRegistryButton && (
          <AddToRegistryButton
            productId={activeProduct.id}
            quantity={quantity}
            useInfoButtonStyle
            buttonStyle={{ pill: true, block: true }}
            style={undefined}
            onSuccess={onAddToRegistrySuccess}
            addToRegistry={addToRegistryAction}
          />
        )}
        <AddToCartButton
          productId={activeProduct.id}
          quantity={quantity}
          useInfoButtonStyle
          buttonStyle={{
            pill: true,
            block: true,
            empty: showAddToRegistryButton,
          }}
          style={undefined}
          onSuccess={onAddToCartSuccess}
          disabled={disableAddToCart}
        />
      </div>
    </div>
  )
}

export default QuickViewModal
